import {
  TbAdjustments,
  TbChartPie,
  TbCurrencyReal,
  TbInfoCircle,
} from "react-icons/tb";
import { StablesLimitsSkeleton } from "../../../../Components/Molecules/DashboardSkeleton";
import intl from "react-intl-universal";
import { PageHeaderWithBack, StablesQuotesSection } from "../../../../Components/Molecules";
import { SummaryCard } from "../../../../Components/Atoms/SummaryCard";
import { formatCurrency } from "../../../../Utils/formatters";
import { useStablesLimits } from "../../../../Hooks/useStablesLimits";
import { STABLES_HOME_PATH } from "../constants";
import { calcLimitPercent, getLimitTone, resolveLimitAvailableCents } from "./constants";
import { StablesWalletGate } from "../StablesWalletGate";
import { StablesLimitUsageList } from "../components/StablesLimitUsageList";

const LimitCategoryCard = ({ category }) => {
  const Icon = category.icon;
  const availableCents = resolveLimitAvailableCents(category);
  const percent = calcLimitPercent(category.usedCents, category.totalCents);
  const tone = getLimitTone(percent);

  return (
    <div className="summary-card min-w-0 w-full">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="summary-card-icon">
            <Icon className="h-4 w-4 text-[#e7a73f]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brand-dark">{category.label}</p>
            <p className="text-xs text-gray-500">{category.description}</p>
          </div>
        </div>
        {category.comingSoon && (
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
            Em breve
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div>
          <p className="text-[11px] text-gray-400">Utilizado</p>
          <p className="text-sm font-bold text-brand-dark">
            {formatCurrency(category.usedCents)}
          </p>
        </div>
        <div>
          <p className="text-[11px] text-gray-400">Disponível</p>
          <p className="text-sm font-bold text-emerald-600">
            {formatCurrency(availableCents)}
          </p>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <p className="text-[11px] text-gray-400">Limite total</p>
          <p className="text-sm font-bold text-brand-dark">
            {formatCurrency(category.totalCents)}
          </p>
        </div>
      </div>

      <div className="cartoes-progress mt-4">
        <div className="cartoes-progress__track">
          <div
            className={`cartoes-progress__fill cartoes-progress__fill--${tone}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="cartoes-progress__label">
          {percent}% do limite utilizado
        </p>
      </div>

      <StablesLimitUsageList usage={category.usage} />
    </div>
  );
};

export const StablesLimits = () => {
  const { items: categories, summary, loading, error, reload } = useStablesLimits();
  const percent = calcLimitPercent(summary.usedCents, summary.totalCents);

  return (
    <div className="w-full">
      <StablesWalletGate>
      <StablesQuotesSection showViewAll={false} />

      <PageHeaderWithBack
        title="Limites transacionais Stables"
        subTitle="Consulte seus limites de saque on-chain, swap e operações com stablecoins."
        backTo={STABLES_HOME_PATH}
      />

      {loading ? (
        <StablesLimitsSkeleton />
      ) : error ? (
        <div className="app-card app-empty-state">
          <p>{intl.get("stables.limitsLoadError")}</p>
          <button type="button" className="btn-outline mt-2 !rounded-full !px-4" onClick={reload}>
            {intl.get("stables.retryLoad")}
          </button>
        </div>
      ) : (
        <>
          <div className="dashboard-card-grid dashboard-card-grid-pix mb-5">
            <SummaryCard
              icon={TbAdjustments}
              title="Limite total"
              value={formatCurrency(summary.totalCents)}
              footer={
                <p className="mt-2 text-xs text-gray-500">
                  Soma dos limites por categoria
                </p>
              }
            />
            <SummaryCard
              icon={TbChartPie}
              title="Utilizado"
              value={formatCurrency(summary.usedCents)}
              footer={
                <p className="mt-2 text-xs text-gray-500">
                  {percent}% do limite consumido
                </p>
              }
            />
            <SummaryCard
              icon={TbCurrencyReal}
              title="Disponível"
              value={formatCurrency(summary.availableCents)}
              footer={
                <p className="mt-2 text-xs text-gray-500">
                  Saldo restante para transações
                </p>
              }
            />
          </div>

          <div className="app-card overflow-hidden">
            <div className="border-b border-gray-100 px-4 py-3">
              <p className="text-sm font-bold text-brand-dark">Limites por categoria</p>
              <p className="text-xs text-gray-500">
                Valores vigentes para cada tipo de operação com stablecoins.
              </p>
            </div>

            <div className="grid gap-3 p-4 md:grid-cols-2">
              {categories.map((category) => (
                <LimitCategoryCard key={category.id} category={category} />
              ))}
            </div>
          </div>
        </>
      )}

      <div className="stables-info-banner mt-4">
        <TbInfoCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#e7a73f]" />
        <div className="text-xs leading-relaxed text-gray-600">
          <p className="font-semibold text-brand-dark">Sobre os limites Stables</p>
          <p className="mt-1">
            Os limites transacionais aplicam-se às operações com stablecoins e podem variar
            conforme o tipo de operação e o perfil da conta. Os valores exibidos são renovados
            automaticamente a cada ciclo. A configuração dos tetos é feita apenas pela
            instituição (administradores Valut).
          </p>
        </div>
      </div>
      </StablesWalletGate>
    </div>
  );
};
